import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, ChevronRight, Users, Settings } from 'lucide-react';
import { workflowStagesService } from '../../services';

// Voyager Pro reskin (Figma WorkflowStagesPage) - already fully unified for
// both agency and independent agents: backend/routes/workflow-stages.js
// scopes every CRUD op by `agent_id = req.user.id` regardless of role, and
// the `agencyDataFilter` middleware no-ops when agency_id is null (true for
// independent agents), so no backend/DB changes were needed here. There is
// a separate, unused independent_workflow_stages table + /independent/workflow
// route/service (0 rows, dead) left in place from an earlier, never-adopted
// approach - not used by this component.

const COLORS = [
  { hex: '#C4871F', label: 'Amber' }, { hex: '#1A2B4A', label: 'Navy' },
  { hex: '#DC2626', label: 'Red' }, { hex: '#059669', label: 'Emerald' },
  { hex: '#2563EB', label: 'Blue' }, { hex: '#7C3AED', label: 'Violet' },
  { hex: '#0891B2', label: 'Teal' }, { hex: '#DB2777', label: 'Pink' },
  { hex: '#D97706', label: 'Orange' }, { hex: '#475569', label: 'Slate' },
];

function StageModal({ initial, defaultOrder, onSave, onClose }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [stageOrder, setStageOrder] = useState(initial?.stage_order ?? defaultOrder ?? 1);
  const [color, setColor] = useState(initial?.color ?? COLORS[0].hex);
  const isEdit = !!initial;
  const valid = name.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="h-1 w-full transition-colors" style={{ backgroundColor: color }} />
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)]">
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-lg text-[var(--foreground)]">{isEdit ? 'Edit Stage' : 'Create Workflow Stage'}</h2>
            <p className="text-xs text-[var(--muted-foreground)] mt-0.5">{isEdit ? 'Update the stage details below.' : 'Add a new stage to your client pipeline.'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider mb-1.5">Stage Name <span className="text-red-500">*</span></label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Lead, Quote Sent, Booked…"
              autoFocus
              className="w-full px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              placeholder="Describe what happens in this stage…"
              className="w-full px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider mb-1.5">Stage Order <span className="text-red-500">*</span></label>
            <input
              type="number"
              min="1"
              value={stageOrder}
              onChange={e => setStageOrder(parseInt(e.target.value) || 1)}
              className="w-full px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider mb-2">Stage Colour</label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map(c => (
                <button
                  key={c.hex}
                  type="button"
                  title={c.label}
                  onClick={() => setColor(c.hex)}
                  className="w-8 h-8 rounded-lg transition-all relative"
                  style={{
                    backgroundColor: c.hex,
                    boxShadow: color === c.hex ? `0 0 0 2px white, 0 0 0 4px ${c.hex}` : 'none',
                    transform: color === c.hex ? 'scale(1.12)' : 'scale(1)',
                  }}
                >
                  {color === c.hex && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="absolute inset-0 m-auto"><polyline points="20 6 9 17 4 12" /></svg>
                  )}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3 p-2.5 rounded-xl border border-[var(--border)] bg-[var(--muted)]/40">
              <div className="w-5 h-5 rounded-md flex-shrink-0" style={{ backgroundColor: color }} />
              <span className="text-xs font-mono text-[var(--muted-foreground)]">{color}</span>
              <span className="text-xs text-[var(--muted-foreground)] ml-1">· {COLORS.find(c => c.hex === color)?.label ?? 'Custom'}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-[var(--border)]">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">Cancel</button>
          <button
            onClick={() => valid && onSave(name.trim(), description.trim(), stageOrder, color)}
            disabled={!valid}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-105 transition-all"
            style={{ backgroundColor: 'var(--primary)' }}
          >
            {isEdit ? 'Save Changes' : 'Create Stage'}
          </button>
        </div>
      </div>
    </div>
  );
}

const WorkflowStages = () => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);

  useEffect(() => { loadStages(); }, []);

  const loadStages = async () => {
    try {
      setLoading(true);
      const stagesData = await workflowStagesService.getStages();
      setStages(stagesData.sort((a, b) => a.stage_order - b.stage_order));
    } catch (error) {
      console.error('Failed to load workflow stages:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNextStageOrder = () => {
    const activeStages = stages.filter(s => s.is_active);
    return activeStages.length > 0 ? Math.max(...activeStages.map(s => s.stage_order)) + 1 : 1;
  };

  const handleCreateStage = async (name, description, stage_order, color) => {
    try {
      await workflowStagesService.createStage({ name, description, stage_order, color });
      setShowCreateModal(false);
      await loadStages();
    } catch (error) {
      console.error('Failed to create stage:', error);
      const message = error.response?.data?.message || 'Failed to create workflow stage. Please try again.';
      alert(`Error: ${message}`);
    }
  };

  const handleUpdateStage = async (name, description, stage_order, color) => {
    try {
      await workflowStagesService.updateStage(selectedStage.id, { name, description, stage_order, color });
      setShowEditModal(false);
      setSelectedStage(null);
      await loadStages();
    } catch (error) {
      console.error('Failed to update stage:', error);
      alert('Failed to update workflow stage. Please try again.');
    }
  };

  const handleDeleteStage = async (stage) => {
    if (!window.confirm(`Are you sure you want to delete the "${stage.name}" stage?`)) return;
    try {
      await workflowStagesService.deleteStage(stage.id);
      await loadStages();
    } catch (error) {
      console.error('Failed to delete stage:', error);
      alert('Failed to delete workflow stage. This stage may have clients assigned to it.');
    }
  };

  const openEditModal = (stage) => { setSelectedStage(stage); setShowEditModal(true); };

  if (loading) {
    return <div className="voyager-app flex items-center justify-center min-h-[400px] text-sm text-[var(--muted-foreground)]">Loading workflow stages…</div>;
  }

  return (
    <div className="voyager-app p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl text-[var(--foreground)] mb-1">Workflow Stages</h1>
            <p className="text-[var(--muted-foreground)] text-sm">Customize your client management pipeline.</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[var(--accent)] text-white rounded-xl text-sm font-semibold hover:brightness-105 transition-all shadow-sm"
          >
            <Plus size={16} />
            Add Stage
          </button>
        </div>

        {stages.length === 0 ? (
          <div className="text-center py-16 bg-white border-2 border-dashed border-[var(--border)] rounded-2xl">
            <Settings size={40} className="mx-auto text-[var(--muted-foreground)] mb-3" />
            <div className="text-sm font-medium text-[var(--foreground)] mb-1">No workflow stages configured</div>
            <div className="text-xs text-[var(--muted-foreground)] mb-4">Create your first workflow stage to start organizing your clients.</div>
            <button onClick={() => setShowCreateModal(true)} className="px-5 py-2.5 bg-[var(--accent)] text-white rounded-xl text-sm font-semibold hover:brightness-105 transition-all inline-flex items-center gap-2">
              <Plus size={16} />
              Create First Stage
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-stretch gap-3">
            {stages.map((stage, index) => (
              <React.Fragment key={stage.id}>
                <div className="bg-white border border-[var(--border)] rounded-xl overflow-hidden w-64 flex-shrink-0">
                  <div className="h-1" style={{ backgroundColor: stage.color }} />
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <div className="w-3 h-3 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: stage.color }} />
                        <div className="min-w-0">
                          <h3 className="text-sm font-bold text-[var(--foreground)] truncate">{stage.name}</h3>
                          {stage.description && <p className="text-xs text-[var(--muted-foreground)] mt-0.5">{stage.description}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => openEditModal(stage)} title="Edit stage" className="p-1.5 rounded-lg hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors"><Edit2 size={14} /></button>
                        <button onClick={() => handleDeleteStage(stage)} title="Delete stage" className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[var(--muted-foreground)] mt-3">
                      <span className="flex items-center gap-1"><Users size={12} />{stage.client_count || 0} {(stage.client_count || 0) === 1 ? 'client' : 'clients'}</span>
                      <span>Order: {stage.stage_order}</span>
                    </div>
                  </div>
                </div>
                {index < stages.length - 1 && (
                  <div className="flex items-center flex-shrink-0 text-[var(--muted-foreground)]"><ChevronRight size={20} /></div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <StageModal onSave={handleCreateStage} onClose={() => setShowCreateModal(false)} defaultOrder={getNextStageOrder()} />
      )}
      {showEditModal && selectedStage && (
        <StageModal initial={selectedStage} onSave={handleUpdateStage} onClose={() => { setShowEditModal(false); setSelectedStage(null); }} />
      )}
    </div>
  );
};

export default WorkflowStages;
